//= require n2words/dist/n2words.js
import { Controller } from '@hotwired/stimulus';

export default class SubscriptionConfirmationController extends Controller {
  static targets = [
    'form',
    'amountInput',
    'amountInWordsInput',
    'inProgressIcon',
    'cancelIcon',
    'checkIcon',
    'confirmButton',
    'amountInWordsTips',
  ];

  static values = {amountInWords: String}

  connect() {
    if (this.hasAmountInWordsInputTarget && this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true;
    }
  }

  limitInputLength(event) {
    const AMOUNT_LENGTH_LIMIT = 9;
    if (event.target.value.length > AMOUNT_LENGTH_LIMIT) {
      event.target.value = event.target.value.slice(0, AMOUNT_LENGTH_LIMIT);
    }
  }

  onAmountKeyup() {
    this.syncAmountInWords();
    this.validateAmountInWords();
  }

  syncAmountInWords() {
    const amount = parseInt(this.amountInputTarget.value, 10) || 0;

    if (amount === 0) {
      this.amountInWordsValue = '';
      return;
    }

    this.amountInWordsInputTarget.removeAttribute('disabled');
    this.amountInWordsValue = n2words(amount, { lang: getCurrentLocale() });
  }

  amountInWordsValueChanged(currentValue) {
    if (this.hasAmountInWordsTipsTarget) {
      this.amountInWordsTipsTarget.innerText = currentValue;
    }
  }

  validateAmountInWords() {
    if (!this.hasAmountInWordsInputTarget) return;

    const amountTyped = this.amountInWordsInputTarget.value;

    if (amountTyped.length <= 2) {
      [this.inProgressIconTarget, this.cancelIconTarget, this.checkIconTarget].forEach(
        el => el.classList.add('d-none')
      );

      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
      return;
    }

    const formatted = this.formatAmountText(this.amountInWordsValue);
    const typed = this.formatAmountText(amountTyped);

    if (formatted === typed) {
      this.inProgressIconTarget.classList.add('d-none');
      this.cancelIconTarget.classList.add('d-none');
      this.checkIconTarget.classList.remove('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = false;
    } else if (formatted.substring(0, typed.length) === typed) {
      this.inProgressIconTarget.classList.remove('d-none');
      this.cancelIconTarget.classList.add('d-none');
      this.checkIconTarget.classList.add('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
    } else {
      this.inProgressIconTarget.classList.add('d-none');
      this.cancelIconTarget.classList.remove('d-none');
      this.checkIconTarget.classList.add('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
    }
  }

  checkAndConfirm(event) {
    if (!this.hasAmountInputTarget) return;
    if (!this.hasFormTarget) return;

    if (!this.formTarget.reportValidity()) return;

    const lossesCapacity = this.amountInputTarget.dataset.losses_capacity;
    const amount = parseInt(this.amountInputTarget.value, 10);

    if (isNaN(amount)) return;

    this.populateConfirmationModal(amount);

    if (lossesCapacity && amount > parseInt(lossesCapacity, 10)) {
      new bootstrap.Modal(document.getElementById('subscriptionWarningModal')).show();
    } else {
      new bootstrap.Modal(document.getElementById('subscriptionConfirmationModal')).show();
    }
  }

  handleWarningAccepted() {
    bootstrap.Modal.getInstance(document.getElementById('subscriptionWarningModal')).hide();
    new bootstrap.Modal(document.getElementById('subscriptionConfirmationModal')).show();
  }

  handleConfirmSubmit() {
    const confirmationModal = document.getElementById('subscriptionConfirmationModal');
    bootstrap.Modal.getInstance(confirmationModal).hide();
    if (confirmationModal.pendingForm) {
      confirmationModal.pendingForm.submit();
    }
  }

  populateConfirmationModal(amount) {
    const confirmationModal = document.getElementById('subscriptionConfirmationModal');
    const amountEl = confirmationModal.querySelector('#amount');
    const projectEl = confirmationModal.querySelector('#project');
    const selectedCurrency = confirmationModal.dataset.projectCurrency;
    const formatter = new Intl.NumberFormat(getCurrentLocale(), { style: 'currency', currency: selectedCurrency });
    amountEl.innerText = formatter.format(amount);
    if (projectEl) {
      projectEl.innerText = confirmationModal.dataset.projectName;
    }
    confirmationModal.pendingForm = this.formTarget;
  }

  formatAmountText(text) {
    return text.normalize('NFD').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

}
